/**
 * The render pass registry: each feature's drawing step (the sky, the warp circles, the night, the
 * name tags, a screen flash …) draws itself from a file of its own in this folder,
 * `render/passes/<pass>.ts`, which calls `registerRenderPass` when it loads. The Renderer
 * (render/renderer.ts) loads every file here and keeps only the core: the camera, the zoom and
 * lens, sorting and drawing the scenery and the figures, the camera turn and the blit to the screen.
 *
 * A frame runs the layers in this order, each pass within its layer by `order` (lowest first):
 *
 * - `ground`: the world picture (`g`, art pixels) under the scenery and the figures.
 * - `world`: the world picture over the scenery and the figures (then the camera turn swirls it).
 * - `screen`: the screen (`mg`, CSS pixels) over the blitted world picture.
 * - `labels`: the screen in the zoomed world's transform (name tags, numbers follow the zoom).
 * - `top`: the screen, unzoomed, last of all.
 *
 * Adding a drawing step is one new file:
 * `registerRenderPass({ id: 'myPass', layer: 'world', order: 450, draw(f) { … } })`.
 * State a pass keeps from frame to frame lives in its `state`, made once per Renderer.
 */
import type { Entity, GameMap, IsoOrigin, World } from '@nine/shared';
import type { IsoCamera } from '../camera';
import type { Effects } from '../effects';
import type { TallGrass } from '../grass';
import type { InteriorScene, InteriorView } from '../interior';
import type { PropGrid } from '../propGrid';
import type { Renderer } from '../renderer';
import type { SpriteBank } from '../sprites';
import type { Ground, Prop, ViewMapping } from '../terrain';
import type { WebGLDevice } from '../webgl';

export type PassLayer = 'ground' | 'world' | 'screen' | 'labels' | 'top';

/** The layers in the order a frame draws them. */
export const PASS_LAYERS: readonly PassLayer[] = ['ground', 'world', 'screen', 'labels', 'top'];

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The current camera angle's outdoor ground and scenery. */
export interface Outdoors {
  readonly ground: Ground;
  readonly props: PropGrid;
  readonly grass: TallGrass;
}

/** One frame, as a pass sees it. */
export interface PassFrame {
  readonly renderer: Renderer;
  readonly world: World;
  readonly effects: Effects;
  readonly sprites: SpriteBank;
  readonly iso: IsoCamera;
  readonly gpu: WebGLDevice;
  /** The world picture, in art pixels (the renderer's resolution already applied). */
  readonly g: CanvasRenderingContext2D;
  /** The screen, in CSS pixels; its transform is set by the layer (see above). */
  readonly mg: CanvasRenderingContext2D;
  /** Seconds since the last frame. */
  readonly dt: number;
  /** World pixel → world picture offset. */
  readonly ox: number;
  readonly oy: number;
  /** The world picture's size, in art pixels. */
  readonly W: number;
  readonly H: number;
  /** The screen's size, in CSS pixels. */
  readonly screenW: number;
  readonly screenH: number;
  /** CSS pixels per art pixel (before the lens). */
  readonly px: number;
  /** Where the camera looks (world pixels). */
  readonly cam: { readonly x: number; readonly y: number };
  /** The screen at the widest zoom, in world-picture pixels, centred on the picture. */
  readonly room: Rect;
  /** The part of the world picture on screen at this zoom. */
  readonly visible: Rect;
  /** The player's ground point (world pixels). */
  readonly player: readonly [number, number];
  readonly indoor: InteriorView;
  /** The building the player is in, or null outdoors. */
  readonly scene: InteriorScene | null;
  /** The ground and scenery outdoors, or null indoors. */
  readonly outdoors: Outdoors | null;
  /** Dressed figures drawn last frame. */
  readonly crowd: number;
  /** The strength of a big move's cinematic dim and letterbox (from the `world` layer on). */
  readonly cine: number;
  /** The entities to draw: each as itself, or as the cutscene puppet standing in for it. */
  shown(): Iterable<Entity>;
  /** Draws a figure on the world picture, as the core draws it. */
  drawEntity(e: Entity): void;
}

/** A camera angle's scenery being built (see `RenderPass.scenery`). */
export interface SceneryView {
  readonly map: GameMap;
  readonly origin: IsoOrigin;
  readonly view: ViewMapping;
  /** That angle's ground, one per angle and Renderer. */
  readonly ground: Ground;
}

export interface RenderPass<S = undefined> {
  /** Unique name. */
  id: string;
  layer: PassLayer;
  /** Place within the layer, lowest first; unique within the layer. */
  order: number;
  /** State kept from frame to frame, made once per Renderer as it is made. */
  state?: () => S;
  /**
   * Called as a camera angle's ground and scenery are built (one angle at a time); the props
   * returned join that angle's scenery, after the map's own.
   */
  scenery?(v: SceneryView, s: S): readonly Prop[];
  draw(f: PassFrame, s: S): void;
}

const passes: RenderPass<unknown>[] = [];

export function registerRenderPass<S = undefined>(pass: RenderPass<S>): void {
  const p = pass as unknown as RenderPass<unknown>;
  if (passes.some((q) => q.id === p.id)) throw new Error(`render pass ${p.id} registered twice`);
  const clash = passes.find((q) => q.layer === p.layer && q.order === p.order);
  if (clash) throw new Error(`render passes ${clash.id} and ${p.id} share ${p.layer} order ${p.order}`);
  passes.push(p);
}

/** Every registered pass, in drawing order. */
export function renderPasses(): readonly RenderPass<unknown>[] {
  return [...passes].sort(
    (a, b) => PASS_LAYERS.indexOf(a.layer) - PASS_LAYERS.indexOf(b.layer) || a.order - b.order,
  );
}

/** One Renderer's passes, each with its state. */
export class PassRunner {
  private readonly layers = new Map<PassLayer, { pass: RenderPass<unknown>; state: unknown }[]>();
  private readonly all: { pass: RenderPass<unknown>; state: unknown }[] = [];

  constructor() {
    for (const pass of renderPasses()) {
      const run = { pass, state: pass.state?.() };
      this.all.push(run);
      let list = this.layers.get(pass.layer);
      if (!list) this.layers.set(pass.layer, (list = []));
      list.push(run);
    }
  }

  /** Draws one layer's passes. */
  draw(layer: PassLayer, f: PassFrame): void {
    for (const { pass, state } of this.layers.get(layer) ?? []) pass.draw(f, state);
  }

  /** The props the passes add to a camera angle's scenery. */
  scenery(v: SceneryView): Prop[] {
    const out: Prop[] = [];
    for (const { pass, state } of this.all) if (pass.scenery) out.push(...pass.scenery(v, state));
    return out;
  }
}
